const API_BASE_URL = (import.meta as any).env.VITE_SUPABASE_URL + '/functions/v1/api';

interface ApiResponse<T> {
  data: T | null;
  error: any;
}

// Data transformation utilities
const transformClass = (cls: any) => ({
  id: cls.id,
  name: cls.name,
  code: cls.code,
  term: cls.term,
  professorId: cls.professor_id,
  createdAt: cls.created_at ? new Date(cls.created_at) : null,
  updatedAt: cls.updated_at ? new Date(cls.updated_at) : null
});

const transformClassSetup = (setup: any) => ({
  id: setup.id,
  classId: setup.class_id,
  frequency: setup.frequency,
  totalClasses: setup.total_classes,
  selectedCategories: setup.selected_categories,
  createdAt: setup.created_at ? new Date(setup.created_at) : null,
  updatedAt: setup.updated_at ? new Date(setup.updated_at) : null
});

const transformStudentProfile = (profile: any) => ({
  id: profile.id,
  userId: profile.user_id,
  classId: profile.class_id,
  responses: profile.responses,
  currentStep: profile.current_step,
  completed: profile.completed,
  backgroundData: profile.background_data,
  createdAt: profile.created_at ? new Date(profile.created_at) : null,
  updatedAt: profile.updated_at ? new Date(profile.updated_at) : null
});

const transformProfile = (profile: any) => ({
  id: profile.id,
  email: profile.email,
  userType: profile.user_type,
  fullName: profile.full_name,
  createdAt: profile.created_at ? new Date(profile.created_at) : null,
  updatedAt: profile.updated_at ? new Date(profile.updated_at) : null
});

class ApiService {
  private async getAuthToken(): Promise<string | null> {
    try {
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      return session?.access_token || null;
    } catch {
      return null;
    }
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    try {
      const token = await this.getAuthToken();
      if (!token) {
        return { data: null, error: 'No authentication token available' };
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...options.headers,
        },
        ...options,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Network error' }));
        return { data: null, error: errorData.error || `HTTP ${response.status}` };
      }

      const data = await response.json();
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  // Class Management
  async createClass(classData: { name: string; code: string; term: string; professor_id: string }) {
    const result = await this.request<any>('/classes', {
      method: 'POST',
      body: JSON.stringify(classData),
    });
    if (result.data) {
      result.data = transformClass(result.data);
    }
    return result;
  }

  async getClassesByProfessor(professorId: string) {
    const endpoint = professorId ? `/classes?professor_id=${professorId}` : '/classes';
    const result = await this.request<any[]>(endpoint);
    if (result.data) {
      result.data = result.data.map(transformClass);
    }
    return result;
  }

  async getAllAvailableClasses() {
    const result = await this.request<any[]>('/classes?include_profile=true');
    if (!result.data) {
      result.data = [];
    } else {
      result.data = result.data.map((item: any) => ({
        class: transformClass(item),
        professor: item.professor ? transformProfile(item.professor) : null
      }));
    }
    return result;
  }

  async getClassById(classId: string) {
    const result = await this.request<any>(`/classes/${classId}`);
    if (result.data) {
      result.data = transformClass(result.data);
    }
    return result;
  }

  async getClassSetup(classId: string) {
    const result = await this.request<any>(`/classes/${classId}/setup`);
    if (result.data) {
      result.data = transformClassSetup(result.data);
    }
    return result;
  }

  async saveClassSetup(classId: string, setup: any) {
    return this.request(`/classes/${classId}/setup`, {
      method: 'POST',
      body: JSON.stringify(setup),
    });
  }

  async getClassStudents(classId: string) {
    const result = await this.request<any[]>(`/classes/${classId}/students`);
    if (!result.data) {
      result.data = [];
    } else {
      result.data = result.data.map((item: any) => ({
        ...item,
        student: item.student ? transformProfile(item.student) : null
      }));
    }
    return result;
  }

  // Profile Management
  async getUserProfile(userId: string) {
    const result = await this.request<any>(`/profiles/${userId}`);
    if (result.data) {
      result.data = transformProfile(result.data);
    }
    return result;
  }

  async createUserProfile(profileData: { 
    id: string; 
    email: string; 
    user_type: 'professor' | 'student'; 
    full_name?: string 
  }) {
    return this.request('/profiles', {
      method: 'POST',
      body: JSON.stringify(profileData),
    });
  }

  // Student Profile Management
  async getStudentProfile(userId: string, classId?: string) {
    const endpoint = classId 
      ? `/student-profiles?user_id=${userId}&class_id=${classId}`
      : `/student-profiles?user_id=${userId}`;
    const result = await this.request<any>(endpoint);
    if (result.data) {
      result.data = transformStudentProfile(result.data);
    }
    return result;
  }

  async updateStudentProfile(userId: string, updateData: {
    backgroundData?: any;
    responses?: any;
    current_step?: number;
    completed?: boolean;
  }) {
    // Transform camelCase to snake_case for API
    const apiData: any = {};
    if (updateData.backgroundData !== undefined) apiData.background_data = updateData.backgroundData;
    if (updateData.responses !== undefined) apiData.responses = updateData.responses;
    if (updateData.current_step !== undefined) apiData.current_step = updateData.current_step;
    if (updateData.completed !== undefined) apiData.completed = updateData.completed;

    return this.request(`/student-profiles/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(apiData),
    });
  }

  async saveStudentProfile(profileData: {
    user_id: string;
    class_id: string;
    responses: any;
    current_step: number;
    completed?: boolean;
  }) {
    return this.request('/student-profiles', {
      method: 'POST',
      body: JSON.stringify(profileData),
    });
  }

  async getStudentProfilesByClass(classId: string) {
    const result = await this.request<any[]>(`/student-profiles/${classId}`);
    if (!result.data) {
      result.data = [];
    } else {
      result.data = result.data.map((item: any) => ({
        ...transformStudentProfile(item),
        profiles: item.profiles ? transformProfile(item.profiles) : null
      }));
    }
    return result;
  }

  // Enrollment Management
  async enrollStudent(enrollmentData: {
    class_id: string;
    student_id: string;
    enrollment_date?: string;
    status?: 'active' | 'inactive' | 'dropped';
  }) {
    return this.request('/enrollments', {
      method: 'POST',
      body: JSON.stringify(enrollmentData),
    });
  }

  async getStudentEnrollments(studentId: string) {
    const result = await this.request<any[]>(`/enrollments?student_id=${studentId}`);
    if (!result.data) {
      result.data = [];
    } else {
      result.data = result.data.map((item: any) => ({
        ...item,
        class: item.class ? transformClass(item.class) : null,
        professor: item.class?.professor ? transformProfile(item.class.professor) : null
      }));
    }
    return result;
  }

  async updateEnrollmentStatus(enrollmentId: string, status: 'active' | 'inactive' | 'dropped') {
    return this.request(`/enrollments/${enrollmentId}`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  // Feedback Session Management
  async createFeedbackSession(sessionData: {
    class_id: string;
    session_number: number;
    questions: any;
  }) {
    return this.request('/feedback-sessions', {
      method: 'POST',
      body: JSON.stringify(sessionData),
    });
  }

  async getFeedbackSessions(classId: string) {
    const result = await this.request<any[]>(`/feedback-sessions?class_id=${classId}`);
    if (!result.data) {
      result.data = [];
    }
    return result;
  }

  // Feedback Response Management
  async saveFeedbackResponse(responseData: {
    student_id: string;
    session_id: string;
    responses: any;
    completed_at?: string;
  }) {
    return this.request('/feedback-responses', {
      method: 'POST',
      body: JSON.stringify(responseData),
    });
  }

  async getStudentFeedbackResponses(studentId: string) {
    return this.request(`/feedback-responses?student_id=${studentId}`);
  }

  // Get student survey results from the get-results Edge Function
  async getStudentSurveyResults(studentId: string) {
    try {
      const token = await this.getAuthToken();
      if (!token) {
        return { data: null, error: 'No authentication token available' };
      }

      const response = await fetch(`${(import.meta as any).env.VITE_SUPABASE_URL}/functions/v1/get-results?user_id=${studentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Network error' }));
        return { data: null, error: errorData.error || `HTTP ${response.status}` };
      }

      const data = await response.json();
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  // Get complete student data with profile and enrollment
  async getStudentWithProfile(studentId: string, classId: string) {
    try {
      // Get user profile
      const profileResult = await this.getUserProfile(studentId);
      
      // Get student profile for the specific class
      const studentProfileResult = await this.getStudentProfile(studentId, classId);
      
      // Get enrollment info (if available)
      const enrollmentResult = await this.getStudentEnrollments(studentId);
      const classEnrollment = enrollmentResult.data?.find((e: any) => e.class?.id === classId);
      
      // Get feedback responses (if available)
      const feedbackResult = await this.getStudentFeedbackResponses(studentId);
      
      return {
        data: {
          profile: profileResult.data,
          studentProfile: studentProfileResult.data,
          enrollment: classEnrollment || null,
          feedbackResponses: feedbackResult.data || []
        },
        error: null
      };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Failed to get student data'
      };
    }
  }
}

export const apiService = new ApiService();